import { openbetHttpClient, securedOpenbetHttpClient } from "@/api/openbet-http-client";
import type { BetParams, BetsParams, BetResponse, BetsResponse, BetCashoutParams } from "@/api/interfaces/bet";
import type { SlipBuildParams, SlipBuildData, SlipBuild } from "@/api/interfaces/slip-build";
import type { SlipBet, SlipBetData } from "@/api/interfaces/slip-bet";
import { appendEnvSuffix } from "@/api/http-client-helpers";
import { BetServiceParams } from "@/api/interfaces/bet-service-response";
import { isEqual as _isEqual } from "lodash";
import { logException } from "@/shared/utils/logger/use-app-logger";
import { ngFrameworkLoaded } from "@/shared/ng/ng-integration";

const mandatoryParams: BetServiceParams = {
    expand: "cashout",
};
const path = appendEnvSuffix("/bet-service");

export async function getBets(params: BetsParams): Promise<BetsResponse> {
    await ngFrameworkLoaded();
    const response = await securedOpenbetHttpClient.get(`${path}/bets`, { params: { ...params, ...mandatoryParams } });
    return response.data;
}

export async function getBet(params: BetParams): Promise<BetResponse> {
    await ngFrameworkLoaded();
    const response = await securedOpenbetHttpClient.get(`${path}/bets/${params.id}`, {
        params: { expand: params.expand || "cashout" },
    });
    return response.data;
}

export async function patchBetCashout(params: BetCashoutParams): Promise<BetResponse> {
    await ngFrameworkLoaded();
    const response = await securedOpenbetHttpClient.patch(
        `${path}/bets/${params.id}/cashout`,
        { acceptedCashoutValue: params.cashoutValue },
        {
            params: { expand: "cashout" },
        },
    );
    return response.data;
}

export async function postSlipBet(data: SlipBetData): Promise<SlipBet> {
    await ngFrameworkLoaded();

    try {
        const response = await securedOpenbetHttpClient.post(`${path}/slips`, data);
        return response.data;
    } catch (error) {
        // TODO: Not 100% sure yet whether we want to bubble all error data... will crystallize later during usage
        //  Btw... this is totally stupid approach by the ep to return error states of the slip as http errors because we'll need to catch all those errors here
        //  Allow only known errors like 422 to be able to return data as SlipBet and log the rest (maybe ignore in the future)
        if ([422].includes(error.response.status)) {
            return error.response.data as SlipBet;
        } else if (error.response.status === 500 && error.response?.data?.errors?.length) {
            // We should know about that we receive 500, even if response contains data.errors which should give us some hint what's wrong and will be processed later
            logException("bet-service", error, {
                message: "An error has occurred while posting slip bet (don't confuse with build slip or slip build)",
                data: data,
            });
            return error.response.data as SlipBet;
        } else {
            logException("bet-service", error, {
                message: "An error has occurred while posting slip bet (don't confuse with build slip or slip build)",
                data: data,
            });
        }
    }
}

export async function postSlipBuild(data: SlipBuildData, params?: SlipBuildParams): Promise<SlipBuild> {
    try {
        const response = await openbetHttpClient.post(`${path}/slips/local/build`, data, { params });
        return response.data;
    } catch (error) {
        // TODO: Not 100% sure yet whether we want to bubble all error data... will crystallize later during usage
        //  Btw... this is totally stupid approach by the ep to return error states of the slip as http errors because we'll need to catch all those errors here
        //  Allow only known errors like 422 to be able to return data as SlipBuild and log the rest (maybe ignore in the future)
        if ([422].includes(error.response.status)) {
            return error.response.data as SlipBuild;
        } else if (_isEqual(error.response.data.data, { status: "BUILD_ERROR" })) {
            logException("bet-service", error, {
                message: "An error has occurred while posting slip build (don't confuse with build slip or slip bet)",
                data: data,
                params: params,
            });
        } else if (
            _isEqual(error.response.data.data, {
                status: 500,
                error: "java.net.SocketException",
                message: "Connection reset",
            })
        ) {
            logException("bet-service", error, {
                message:
                    "Connection reset error occurred while posting slip build (don't confuse with build slip or slip bet)",
                data: data,
                params: params,
            });
        }
    }
}

// helpers
function getUrlParam(url: string, param: string): string | undefined {
    if (!url) return undefined;
    const urlParams = new URL(url).searchParams;
    return urlParams.get(param);
}
export function extractPaginationToken(response: BetsResponse): string | undefined {
    const paginationNext = response.actions?.find((action) => action.actionTypeRef === "PAGINATION.NEXT");
    return getUrlParam(paginationNext?.href, "paginationToken");
}
